import React, { useCallback } from 'react';
import { IntlShape } from 'react-intl';
import { RefHandler } from 'react-popper';
import { useLocation } from 'react-router';
import { App, GQL, View } from '@gin-betting/core';
import cn from 'classnames';

import { BetslipAttrManager } from 'app-constants';
import messages from 'messages';
import {
    Category,
    formatPathname,
    formatSearch,
    useAnalyticAttrs,
} from 'services/analytics';
import { Target, tracker } from 'services/tracking-manager';
import { formatDecimalOdd } from 'utils';
import Icon from '../Icon';
import OddStatistic from '../OddStatistic';
import useOdd from './useOdd';

import arrow from 'assets/svg/arrow.svg';
import lock from 'assets/svg/lock.svg';
import './odd.scss';

type Btn = React.DOMAttributes<HTMLButtonElement>;

interface Props {
    odd: View.Odd | null;
    isActive: boolean;
    isCompact: boolean;
    oddFormat: App.OddFormat;
    betslipOddId?: string;
    isLabel?: boolean;
    isSelected?: boolean;
    toggleBetslipOdd?: () => void;
    showResult?: boolean;
    withTitle?: boolean;
    isShowStatistic?: boolean;
    statisticPercent?: number | null;
    onMouseEnter?: Btn['onMouseLeave'];
    onMouseLeave?: Btn['onMouseEnter'];
    fRef?: RefHandler;
    modifyOddTitle?: (name: string) => string;
}

function getOddTittle(
    oddValue: React.ReactText,
    isResultSettled: boolean,
    odd: View.Odd,
    isActive: boolean,
    intl: IntlShape
) {
    const { name, status } = odd;

    if (isActive) {
        return `${name}: ${oddValue}`;
    }

    if (isResultSettled) {
        const resultText = intl.formatMessage(
            status === GQL.OddStatus.WIN ? messages.won : messages.lost
        );

        return `${name}: ${resultText}`;
    }

    return intl.formatMessage(messages.deactivated);
}

const Odd: React.FC<Props> = props => {
    const {
        odd,
        isSelected,
        isLabel,
        isActive,
        oddFormat,
        showResult,
        withTitle,
        isShowStatistic,
        statisticPercent,
        onMouseLeave,
        onMouseEnter,
        fRef,
        modifyOddTitle,
        betslipOddId,
        toggleBetslipOdd,
    } = props;
    const location = useLocation();

    const { search, pathname, hash } = location;

    const analyticAttrs = useAnalyticAttrs();

    const { isIncreased, intl } = useOdd({ odd, isActive });

    const onClickBtn = useCallback(() => {
        if (!toggleBetslipOdd) return;

        toggleBetslipOdd();

        if (!betslipOddId) return;

        if (isSelected) {
            delete BetslipAttrManager[betslipOddId];

            return;
        }

        const selectUrl = `${formatPathname(pathname)}${formatSearch(
            search
        )}${hash}`;

        BetslipAttrManager[betslipOddId] = {
            category: analyticAttrs['data-category'] || Category.Unknown,
            label: { selectUrl },
        };
    }, [
        toggleBetslipOdd,
        betslipOddId,
        analyticAttrs,
        isSelected,
        search,
        pathname,
        hash,
    ]);

    if (odd === null) return null;

    const { value, status, name } = odd;

    const {
        value: formattedValue,
        component: formatComponent,
    } = formatDecimalOdd(value, oddFormat);

    const win = status === GQL.OddStatus.WIN;
    const loss = status === GQL.OddStatus.LOSS;
    const isResultSettled = !!showResult && (win || loss);

    const oddTitle = getOddTittle(
        formattedValue,
        isResultSettled,
        odd,
        isActive,
        intl
    );

    return (
        <React.Fragment>
            {withTitle && (
                <div className="__app-Odd-title" styleName="title" title={name}>
                    <span>
                        {typeof modifyOddTitle === 'function'
                            ? modifyOddTitle(name)
                            : name}
                    </span>
                </div>
            )}
            <button
                onMouseLeave={onMouseLeave}
                onMouseEnter={onMouseEnter}
                ref={fRef}
                {...tracker().markTarget(isSelected ? null : Target.$BetAdd)}
                styleName={cn('button', {
                    'is-selected': isSelected && !isLabel,
                    'is-increasing': isIncreased,
                    'is-decreasing': isIncreased === false,
                    'is-disabled': !isActive,
                    'is-label': isLabel,
                })}
                className={cn('__app-Odd-button', {
                    '__app-Odd-is-selected': isSelected && !isLabel,
                    '__app-Odd-is-disabled': !isActive,
                    '__app-Odd-is-decreasing': isIncreased === false,
                    '__app-Odd-is-increasing': isIncreased,
                })}
                data-category={analyticAttrs['data-category']}
                data-action={analyticAttrs['data-action']}
                data-label={betslipOddId}
                onClick={isSelected || isActive ? onClickBtn : undefined}
                title={oddTitle}
            >
                {isActive ? (
                    <div styleName="inner" className="__app-Odd-inner">
                        {isIncreased != null && (
                            <div
                                className="__app-OddButton-arrow-wrapper"
                                styleName="arrow-wrapper"
                            >
                                <Icon
                                    glyph={arrow}
                                    styleName="arrow"
                                    className="__app-OddButton-arrow"
                                />
                            </div>
                        )}
                        <div styleName="ellipsis">{formatComponent}</div>
                        <OddStatistic
                            show={isShowStatistic}
                            percent={statisticPercent}
                        />
                    </div>
                ) : isResultSettled ? (
                    <div styleName="result" className="__app-Odd-result">
                        <div styleName="ellipsis">
                            {intl.formatMessage(
                                win ? messages.won : messages.lost
                            )}
                        </div>
                    </div>
                ) : (
                    <Icon
                        glyph={lock}
                        styleName="lock"
                        className="__app-Odd-lock"
                    />
                )}

                {isResultSettled && (
                    <div
                        styleName={cn('backlight', {
                            win,
                            loss,
                        })}
                        className={cn('__app-OddButton-backlight', {
                            ['__app-OddButton-win']: win,
                            ['__app-OddButton-loss']: loss,
                        })}
                    />
                )}
            </button>
        </React.Fragment>
    );
};

export default React.memo(Odd);
